import { apiClient } from "@/lib/api/client";
import { isPaginated, type Paginated } from "@/lib/api/pagination";

/**
 * The page size these list endpoints will actually honour.
 *
 * `StandardResultsSetPagination` caps `page_size` at 100 and silently clamps
 * anything larger, so asking for 500 does not fail — it returns 100 and a
 * `next` link. Two callers were doing exactly that and treating the answer as
 * the whole set, which is the same defect as not asking at all, moved from 26
 * records to 101.
 */
const MAX_PAGE_SIZE = 100;

/**
 * Stop the loop rather than let a server that always answers `next` spin.
 *
 * 200 pages is 20,000 organizations, far past any real tenant. Reaching it
 * throws, because a list that is quietly short is the failure this module
 * exists to remove — the caller gets an error it can surface instead of a
 * truncated list it cannot tell from a complete one.
 */
const MAX_PAGES = 200;

/**
 * Every record the caller is authorized to see, not the server's first page.
 *
 * These endpoints paginate at 25 by default. The management screens asked for
 * no page size and rendered `results` as though it were the whole list, with no
 * pagination control, no total and nothing saying a page had been cut — so a
 * distributor with 26 MSPs simply could not see or manage the 26th, and had no
 * way to know it was missing. `count` and `next` were dropped at this layer,
 * so no caller could have detected it even if it wanted to.
 *
 * Rows are keyed by `id`: an organization can move between pages while the loop
 * runs — the default ordering is by name and a concurrent rename or insert
 * reorders the set — and without the guard it would appear twice.
 */
export async function fetchAllPages<T extends { id: string | number }>(
  url: string,
  headers?: Record<string, string>,
): Promise<T[]> {
  const byId = new Map<string | number, T>();

  for (let page = 1; page <= MAX_PAGES; page += 1) {
    const response = await apiClient.get<Paginated<T> | T[]>(url, {
      params: { page, page_size: MAX_PAGE_SIZE },
      headers,
    });
    const body = response.data;

    // An endpoint that is not paginated at all answers a bare array. One pass
    // and it is done — there is no `next` to follow.
    if (!isPaginated<T>(body)) {
      for (const row of Array.isArray(body) ? body : []) byId.set(row.id, row);
      return [...byId.values()];
    }

    for (const row of body.results) byId.set(row.id, row);
    if (!body.next) return [...byId.values()];
  }

  throw new Error(
    `Stopped after ${MAX_PAGES} pages of ${url}; the list is longer than this screen can load.`,
  );
}
